import { motion } from "framer-motion";
import { BrainCircuit, ChartNoAxesCombined, Workflow, Users, ShieldCheck, Rocket } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const platformFeatures = [
  {
    icon: BrainCircuit,
    title: "AI Candidate Scoring",
    description:
      "Our AI evaluates candidate fit using experience depth, role alignment, and historical performance data to surface the most relevant talent instantly.",
  },
  {
    icon: Users,
    title: "Vendor Collaboration",
    description:
      "Bring internal hiring teams and external recruiting partners together in one collaborative platform with complete transparency and performance tracking.",
  },
  {
    icon: Workflow,
    title: "Structured Hiring Workflows",
    description:
      "Design repeatable hiring pipelines with customizable stages, governance checkpoints, and automated decision support.",
  },
  {
    icon: ChartNoAxesCombined,
    title: "Real-Time Hiring Insights",
    description:
      "Monitor pipeline health, recruiter performance, and hiring velocity with real-time dashboards and analytics.",
  },
];

const benefits = [
  {
    icon: Rocket,
    title: "Faster Hiring Cycles",
    description:
      "Reduce hiring timelines by automating screening, prioritizing top candidates, and aligning recruiters with hiring managers instantly.",
  },
  {
    icon: ShieldCheck,
    title: "Quality & Governance",
    description:
      "Maintain consistent hiring standards with structured workflows, role-based permissions, and measurable performance metrics.",
  },
];

export const PlatformPage = () => {
  return (
    <div className="mx-auto max-w-7xl space-y-20 px-6 pb-20 pt-16 md:px-10">

      {/* HERO */}
      <motion.section
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
      >
        <p className="text-sm uppercase tracking-[0.2em] text-[#6FE3C1]">
          Platform
        </p>

        <h1 className="mt-4 text-4xl font-bold md:text-5xl">
          The AI Matters Hiring Platform
        </h1>

        <p className="mt-6 max-w-3xl text-[#D9E1E8]/80 text-lg">
          A modern hiring infrastructure designed to bring AI intelligence,
          recruiter networks, and structured decision workflows into one
          unified platform. The AI Matters helps organizations scale hiring
          programs faster while maintaining quality and governance.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row gap-4">

          <a
            href="https://jobscorematters.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block rounded-full bg-[#2EC4A6] px-8 py-3 font-semibold text-[#071A2C] hover:bg-[#6FE3C1]"
          >
            Access JobScore Portal
          </a>

          <a
            href="/contact"
            className="inline-block rounded-full border border-[#2EC4A6] px-8 py-3 font-semibold text-[#6FE3C1] hover:bg-[#2EC4A6] hover:text-[#071A2C]"
          >
            Request Platform Demo
          </a>

        </div>
      </motion.section>

      {/* FEATURE GRID */}
      <section className="grid gap-6 md:grid-cols-2">
        {platformFeatures.map((feature, index) => {
          const Icon = feature.icon;
          return (
            <Card key={feature.title} className="border-white/10 bg-[#0B253C]/60">
              <CardHeader>
                <div className="mb-3 inline-flex w-fit rounded-full bg-[#2EC4A6]/15 p-2 text-[#6FE3C1]">
                  <Icon size={18} />
                </div>

                <CardTitle className="text-xl">
                  {feature.title}
                </CardTitle>
              </CardHeader>

              <CardContent>
                <p className="text-[#D9E1E8]/80">
                  {feature.description}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </section>

      {/* BENEFITS */}
      <section>
        <h2 className="text-3xl font-bold mb-8">
          Why Companies Choose Our Platform
        </h2>

        <div className="grid gap-6 md:grid-cols-2">
          {benefits.map((item) => {
            const Icon = item.icon;

            return (
              <Card key={item.title} className="border-white/10 bg-[#0B253C]/60">
                <CardHeader>
                  <div className="mb-3 inline-flex w-fit rounded-full bg-[#2EC4A6]/15 p-2 text-[#6FE3C1]">
                    <Icon size={18} />
                  </div>

                  <CardTitle>{item.title}</CardTitle>
                </CardHeader>

                <CardContent>
                  <p className="text-[#D9E1E8]/80">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="glass-panel rounded-2xl p-10 text-center">
        <h2 className="text-3xl font-bold mb-4">
          Transform Your Hiring Infrastructure
        </h2>

        <p className="max-w-2xl mx-auto text-[#D9E1E8]/80 mb-6">
          The AI Matters platform enables companies to combine AI-powered
          candidate intelligence, recruiter networks, and structured hiring
          governance in one scalable operating layer.
        </p>

        <a
          href="https://jobscorematters.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block rounded-full bg-[#2EC4A6] px-8 py-3 font-semibold text-[#071A2C] hover:bg-[#6FE3C1]"
        >
          Go to JobScore Matters Portal
        </a>
      </section>

    </div>
  );
};